import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
  Mail,
  Check,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import BrandMark from "../components/app/BrandMark";
import { useAuth } from "../context/AuthContext";

/* ===========================================================================
   Shared atoms
   =========================================================================== */

const PageShell = ({ children, maxWidth = 460, withGlow = true, dataTestId }) => (
  <div
    className="min-h-screen relative flex items-center justify-center px-4 py-10"
    style={{ background: "var(--color-background-primary)" }}
    data-testid={dataTestId}
  >
    {withGlow && <div className="absolute inset-0 hero-glow pointer-events-none" />}
    <div
      className="relative w-full fade-up"
      style={{
        maxWidth,
        background: "var(--color-background-secondary)",
        border: "1px solid var(--color-border-primary)",
        borderRadius: 20,
        padding: "40px 36px",
        boxShadow: "0 30px 80px -30px rgba(0,0,0,0.65)",
      }}
    >
      {children}
    </div>
  </div>
);

const CardHeader = ({ title, subtitle }) => (
  <div className="text-center mb-8">
    <div className="mb-5 flex justify-center">
      <BrandMark />
    </div>
    <h1
      className="text-text-primary"
      style={{
        fontSize: 28,
        fontWeight: 700,
        letterSpacing: "-0.025em",
        lineHeight: 1.15,
      }}
    >
      {title}
    </h1>
    {subtitle && (
      <p
        className="text-text-secondary mt-2.5 mx-auto"
        style={{ fontSize: 13.5, lineHeight: 1.55, maxWidth: 340 }}
      >
        {subtitle}
      </p>
    )}
  </div>
);

const FieldLabel = ({ children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="block mb-2 text-text-primary"
    style={{ fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em" }}
  >
    {children}
  </label>
);

const TextInput = ({ id, type = "text", value, onChange, placeholder, autoComplete, rightSlot, error, testId, autoFocus, mono = false }) => (
  <div>
    <div className="relative">
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-testid={testId}
        autoFocus={autoFocus}
        className={`w-full outline-none motion-micro ${mono ? "font-mono" : ""}`}
        style={{
          height: 46,
          background: "var(--color-background-tertiary)",
          color: "var(--color-text-primary)",
          border: `1px solid ${error ? "var(--color-danger)" : "var(--color-border-primary)"}`,
          borderRadius: 10,
          paddingLeft: 14,
          paddingRight: rightSlot ? 44 : 14,
          fontSize: 14,
        }}
        onFocus={(e) => {
          if (!error) e.currentTarget.style.borderColor = "var(--color-accent)";
        }}
        onBlur={(e) => {
          if (!error) e.currentTarget.style.borderColor = "var(--color-border-primary)";
        }}
      />
      {rightSlot && <div className="absolute right-2 top-1/2 -translate-y-1/2">{rightSlot}</div>}
    </div>
    {error && (
      <span
        className="block mt-2"
        style={{ fontSize: 12, color: "var(--color-danger)" }}
        data-testid={`${testId}-error`}
      >
        {error}
      </span>
    )}
  </div>
);

const PasswordEye = ({ on, onToggle, testId }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testId}
    aria-label={on ? "Hide password" : "Show password"}
    className="grid place-items-center text-text-tertiary hover:text-text-primary motion-micro"
    style={{
      width: 32,
      height: 32,
      background: "transparent",
      border: "none",
      cursor: "pointer",
    }}
  >
    {on ? <EyeOff size={16} /> : <Eye size={16} />}
  </button>
);

const PrimaryButton = ({ children, onClick, type = "button", disabled, loading, testId, fullWidth = true }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || loading}
    data-testid={testId}
    className="motion-micro inline-flex items-center justify-center gap-2"
    style={{
      height: 46,
      width: fullWidth ? "100%" : undefined,
      padding: fullWidth ? undefined : "0 22px",
      background: "var(--color-accent)",
      color: "#0a0a0a",
      borderRadius: 10,
      border: "none",
      fontSize: 14,
      fontWeight: 600,
      letterSpacing: "-0.005em",
      cursor: disabled || loading ? "not-allowed" : "pointer",
      opacity: disabled || loading ? 0.7 : 1,
      boxShadow: "0 14px 36px -14px rgba(45,212,191,0.55)",
    }}
    onMouseEnter={(e) => {
      if (!disabled && !loading) e.currentTarget.style.background = "var(--color-accent-hover)";
    }}
    onMouseLeave={(e) => {
      if (!disabled && !loading) e.currentTarget.style.background = "var(--color-accent)";
    }}
  >
    {loading ? <span className="skeleton" style={{ width: 80, height: 12, background: "rgba(10,10,10,0.35)" }} /> : children}
  </button>
);

const Checkbox = ({ checked, onChange, label, testId }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={testId}>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="grid place-items-center motion-micro"
      style={{
        width: 16,
        height: 16,
        borderRadius: 4,
        background: checked ? "var(--color-accent)" : "var(--color-background-tertiary)",
        border: `1px solid ${checked ? "var(--color-accent)" : "var(--color-border-primary)"}`,
        cursor: "pointer",
      }}
      aria-checked={checked}
      role="checkbox"
    >
      {checked && <Check size={11} strokeWidth={3} color="#0a0a0a" />}
    </button>
    <span className="text-text-secondary" style={{ fontSize: 13 }}>
      {label}
    </span>
  </label>
);

const LinkButton = ({ children, onClick, testId, color = "var(--color-accent)" }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="motion-micro"
    style={{
      background: "transparent",
      border: "none",
      cursor: "pointer",
      color,
      fontSize: 13,
      fontWeight: 500,
      padding: 0,
    }}
  >
    {children}
  </button>
);

/* ===========================================================================
   Sub-screens
   =========================================================================== */

const SignInScreen = ({ goForgot, goMagic }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!email) return setErrors({ email: "Email is required." });
    if (!password) return setErrors({ password: "Password is required." });
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back", { description: email });
      navigate("/dashboard", { replace: true });
    } else {
      toast.error("Sign in failed", { description: res.error });
      setErrors({ password: res.error });
    }
  };

  return (
    <PageShell dataTestId="signin-screen">
      <CardHeader
        title="Administrator sign in"
        subtitle="Use the private administrator account to access Guided Sentry."
      />

      <form onSubmit={submit} className="space-y-4" data-testid="signin-form">
        <div>
          <FieldLabel htmlFor="signin-email">Email address</FieldLabel>
          <TextInput
            id="signin-email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="name@company.com"
            autoComplete="email"
            testId="email-input"
            error={errors.email}
            mono
            autoFocus
          />
        </div>
        <div>
          <FieldLabel htmlFor="signin-pwd">Password</FieldLabel>
          <TextInput
            id="signin-pwd"
            type={showPwd ? "text" : "password"}
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            autoComplete="current-password"
            testId="password-input"
            error={errors.password}
            rightSlot={<PasswordEye on={showPwd} onToggle={() => setShowPwd((v) => !v)} testId="password-toggle" />}
          />
        </div>

        <div className="flex items-center justify-between pt-1">
          <Checkbox
            checked={remember}
            onChange={setRemember}
            label="Remember me for 30 days"
            testId="remember-me"
          />
          <LinkButton onClick={goForgot} testId="forgot-password-link">
            Forgot password?
          </LinkButton>
        </div>

        <PrimaryButton type="submit" loading={loading} testId="auth-submit-btn">
          Sign In
        </PrimaryButton>
      </form>

      <div className="text-center mt-6 flex flex-col gap-2.5">
        <button
          type="button"
          onClick={goMagic}
          data-testid="magic-link-toggle"
          className="motion-micro text-text-tertiary hover:text-text-primary"
          style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 12 }}
        >
          Prefer passwordless? Send me a magic link
        </button>
      </div>
    </PageShell>
  );
};

const SignUpScreen = ({ goSignin }) => {
  // Keep old bookmarked signup links useful without exposing registration.
  return (
    <PageShell dataTestId="registration-closed-screen">
      <CardHeader
        title="Registration is closed"
        subtitle="Guided Sentry is currently in private access. Please return when public access opens."
      />
      <div className="text-center">
        <LinkButton onClick={goSignin} testId="back-to-signin-link">
          Back to sign in
        </LinkButton>
      </div>
    </PageShell>
  );

};

const ForgotScreen = ({ onSent, goSignin, prefillEmail }) => {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState(prefillEmail || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return setError("Email is required.");
    setLoading(true);
    const res = await forgotPassword(email);
    setLoading(false);
    if (res.ok) {
      toast.success("Code sent", { description: `If the email exists, a 6-digit code is on its way to ${email}.` });
      onSent(email);
    } else {
      toast.error("Couldn't send code", { description: res.error });
    }
  };

  return (
    <PageShell dataTestId="forgot-screen">
      <CardHeader
        title="Reset your password"
        subtitle={"Enter the email address on your account and we'll send you a 6-digit verification code."}
      />
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div>
          <FieldLabel htmlFor="fp-email">Email address</FieldLabel>
          <TextInput
            id="fp-email"
            type="email"
            value={email}
            onChange={(v) => { setEmail(v); setError(""); }}
            placeholder="name@company.com"
            autoComplete="email"
            testId="forgot-email-input"
            error={error}
            mono
            autoFocus
          />
        </div>
        <PrimaryButton type="submit" loading={loading} testId="forgot-submit-btn">
          Send Code
        </PrimaryButton>
      </form>
      <div className="text-center mt-6">
        <LinkButton onClick={goSignin} testId="back-to-signin-link">
          ← Back to sign in
        </LinkButton>
      </div>
    </PageShell>
  );
};

function maskEmail(email) {
  if (!email || !email.includes("@")) return email;
  const [local, domain] = email.split("@");
  const visible = local.slice(0, 1);
  return `${visible}${"*".repeat(Math.max(local.length - 1, 1))}@${domain}`;
}

const VerifyScreen = ({ email, onVerified, goBack }) => {
  const { verifyResetCode, forgotPassword } = useAuth();
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(10 * 60);
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  const code = digits.join("");

  const setDigit = (i, v) => {
    const ch = (v || "").replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[i] = ch;
      return next;
    });
    if (ch && i < 5) inputs.current[i + 1]?.focus();
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) inputs.current[i + 1]?.focus();
  };

  const onPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    txt.split("").forEach((c, i) => (next[i] = c));
    setDigits(next);
    inputs.current[Math.min(txt.length, 5)]?.focus();
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return toast.error("Enter all 6 digits");
    setLoading(true);
    const res = await verifyResetCode(email, code);
    setLoading(false);
    if (res.ok) {
      toast.success("Code verified");
      onVerified(res.reset_token);
    } else {
      toast.error("Couldn't verify", { description: res.error });
    }
  };

  const resend = async () => {
    setDigits(["", "", "", "", "", ""]);
    setSecondsLeft(10 * 60);
    const res = await forgotPassword(email);
    if (res.ok) toast.success("Code re-sent");
    else toast.error("Couldn't resend", { description: res.error });
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <PageShell dataTestId="verify-screen">
      <CardHeader
        title="Check your email"
        subtitle={`We sent a 6-digit code to ${maskEmail(email)}`}
      />
      <form onSubmit={verify} className="space-y-5" data-testid="verify-form">
        <div className="flex justify-center gap-2.5" onPaste={onPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKey(i, e)}
              inputMode="numeric"
              maxLength={1}
              data-testid={`otp-${i}`}
              className="font-mono outline-none text-center motion-micro"
              style={{
                width: 48,
                height: 56,
                background: "var(--color-background-tertiary)",
                border: `1.5px solid ${d ? "var(--color-accent)" : "var(--color-border-primary)"}`,
                borderRadius: 10,
                color: "var(--color-text-primary)",
                fontSize: 22,
                fontWeight: 600,
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
              onBlur={(e) =>
                (e.currentTarget.style.borderColor = d
                  ? "var(--color-accent)"
                  : "var(--color-border-primary)")
              }
            />
          ))}
        </div>
        <p
          className="text-center text-text-secondary font-mono"
          style={{ fontSize: 12, letterSpacing: "0.04em" }}
          data-testid="otp-timer"
        >
          Code expires in {mm}:{ss}
        </p>
        <PrimaryButton type="submit" loading={loading} testId="verify-submit-btn">
          Verify Code
        </PrimaryButton>
      </form>
      <div className="text-center mt-6 flex flex-col gap-2.5">
        <LinkButton onClick={resend} testId="resend-link">
          Didn&apos;t receive it? Resend code
        </LinkButton>
        <LinkButton onClick={goBack} testId="verify-back-link" color="var(--color-text-tertiary)">
          ← Back
        </LinkButton>
      </div>
    </PageShell>
  );
};

const NewPasswordScreen = ({ resetToken, onDone }) => {
  const { resetPassword } = useAuth();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    if (pwd.length < 8) return setErrors({ pwd: "Use at least 8 characters." });
    if (pwd !== confirm) return setErrors({ confirm: "Passwords don't match." });
    setLoading(true);
    const res = await resetPassword(resetToken, pwd);
    setLoading(false);
    if (res.ok) {
      toast.success("Password updated", { description: "Sign in with your new password." });
      onDone();
    } else {
      toast.error("Couldn't update", { description: res.error });
    }
  };

  return (
    <PageShell dataTestId="newpwd-screen">
      <CardHeader
        title="Set a new password"
        subtitle={"Please choose a secure password you haven't used before."}
      />
      <form onSubmit={submit} className="space-y-4" data-testid="newpwd-form">
        <div>
          <FieldLabel htmlFor="np-pwd">New password</FieldLabel>
          <TextInput
            id="np-pwd"
            type={showPwd ? "text" : "password"}
            value={pwd}
            onChange={setPwd}
            placeholder="••••••••"
            autoComplete="new-password"
            testId="newpwd-input"
            error={errors.pwd}
            rightSlot={<PasswordEye on={showPwd} onToggle={() => setShowPwd((v) => !v)} testId="newpwd-toggle" />}
            autoFocus
          />
          <p className="text-text-tertiary mt-2" style={{ fontSize: 11.5 }}>
            Minimum 8 characters
          </p>
        </div>
        <div>
          <FieldLabel htmlFor="np-confirm">Confirm new password</FieldLabel>
          <TextInput
            id="np-confirm"
            type={showConfirm ? "text" : "password"}
            value={confirm}
            onChange={setConfirm}
            placeholder="••••••••"
            autoComplete="new-password"
            testId="newpwd-confirm-input"
            error={errors.confirm}
            rightSlot={<PasswordEye on={showConfirm} onToggle={() => setShowConfirm((v) => !v)} testId="newpwd-confirm-toggle" />}
          />
        </div>
        <PrimaryButton type="submit" loading={loading} testId="newpwd-submit-btn">
          Update Password
        </PrimaryButton>
      </form>
    </PageShell>
  );
};

const MagicLinkScreen = ({ goSignin, prefillEmail }) => {
  const { sendMagicLink } = useAuth();
  const [email, setEmail] = useState(prefillEmail || "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return setError("Email is required.");
    setLoading(true);
    const res = await sendMagicLink(email);
    setLoading(false);
    if (res.ok) {
      setSent(true);
      toast.success("Magic link sent");
    } else {
      toast.error("Couldn't send link", { description: res.error });
    }
  };

  if (sent) {
    return (
      <PageShell dataTestId="magic-success-screen">
        <CardHeader
          title="Check your inbox"
          subtitle={`We sent a sign-in link to ${email}. Open it on this device to continue.`}
        />
        <div
          className="flex justify-center mb-6"
          data-testid="magic-link-success"
        >
          <span
            className="grid place-items-center rounded-full"
            style={{
              width: 56, height: 56,
              background: "rgba(34,197,94,0.12)",
              border: "1px solid var(--color-success)",
            }}
          >
            <CheckCircle2 size={24} color="var(--color-success)" />
          </span>
        </div>
        <div className="text-center">
          <LinkButton onClick={goSignin} testId="magic-back-link">
            ← Back to sign in
          </LinkButton>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell dataTestId="magic-screen">
      <CardHeader
        title="Sign in with magic link"
        subtitle="Enter your email and we'll send you a one-time sign-in link. No password required."
      />
      <form onSubmit={submit} className="space-y-4" data-testid="magic-form">
        <div>
          <FieldLabel htmlFor="ml-email">Email address</FieldLabel>
          <TextInput
            id="ml-email"
            type="email"
            value={email}
            onChange={(v) => { setEmail(v); setError(""); }}
            placeholder="name@company.com"
            autoComplete="email"
            testId="magic-email-input"
            error={error}
            mono
            autoFocus
          />
        </div>
        <PrimaryButton type="submit" loading={loading} testId="magic-submit-btn">
          <Mail size={15} /> Send link
        </PrimaryButton>
      </form>
      <div className="text-center mt-6">
        <LinkButton onClick={goSignin} testId="back-to-signin-link">
          ← Use a password instead
        </LinkButton>
      </div>
    </PageShell>
  );
};

/* ===========================================================================
   Outer: screen state machine + url sync
   =========================================================================== */

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // screens: signin | signup | forgot | verify | newpwd | magic
  const initial = (() => {
    const mode = params.get("mode");
    if (mode === "signup") return "signup";
    if (mode === "forgot") return "forgot";
    if (mode === "magic") return "magic";
    return "signin";
  })();
  const [screen, setScreen] = useState(initial);
  const [emailInFlight, setEmailInFlight] = useState("");
  const [resetToken, setResetToken] = useState("");

  useEffect(() => {
    if (user && user !== false && user !== null) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  switch (screen) {
    case "signup":
      return <SignUpScreen goSignin={() => setScreen("signin")} />;
    case "forgot":
      return (
        <ForgotScreen
          prefillEmail={emailInFlight}
          goSignin={() => setScreen("signin")}
          onSent={(em) => {
            setEmailInFlight(em);
            setScreen("verify");
          }}
        />
      );
    case "verify":
      return (
        <VerifyScreen
          email={emailInFlight}
          goBack={() => setScreen("forgot")}
          onVerified={(t) => {
            setResetToken(t);
            setScreen("newpwd");
          }}
        />
      );
    case "newpwd":
      return (
        <NewPasswordScreen
          resetToken={resetToken}
          onDone={() => setScreen("signin")}
        />
      );
    case "magic":
      return (
        <MagicLinkScreen
          prefillEmail={emailInFlight}
          goSignin={() => setScreen("signin")}
        />
      );
    case "signin":
    default:
      return (
        <SignInScreen
          goForgot={() => setScreen("forgot")}
          goMagic={() => setScreen("magic")}
        />
      );
  }
}
